import React, { useState } from "react";
import { connect } from "react-redux";
import { RootState } from "../../../store";
import { UserActionCreator } from "../../../store/userActions";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/delete.svg";
import {Button} from "../../../components/common/Button";

type Comment = {
    id: number | string;
    text: string;
};

type OwnProps = {
    clientId: number | string;
    dealId: number | string;
    comments: Comment[];
};

type DispatchProps = {
    addClientDealComment: (payload: {
        clientId: number | string;
        dealId: number | string;
        text: string;
    }) => any;
    updateClientDealComment: (payload: {
        commentId: number | string;
        text: string;
    }) => any;
    deleteClientDealComment: (payload: { commentId: number | string }) => any;
    getClientDeals: (payload: { clientId: number | string }) => any;
};

type Props = OwnProps & DispatchProps;

const _DealComments: React.FC<Props> = ({
                                            clientId,
                                            dealId,
                                            comments,
                                            addClientDealComment,
                                            updateClientDealComment,
                                            deleteClientDealComment,
                                            getClientDeals,
                                        }) => {
    // добавление
    const [newText, setNewText] = useState("");
    const [addPending, setAddPending] = useState(false);

    // редактирование
    const [editingId, setEditingId] = useState<number | string | null>(null);
    const [editingText, setEditingText] = useState("");
    const [editPending, setEditPending] = useState(false);

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        const text = newText.trim();
        if (!text) return;

        try {
            setAddPending(true);
            await addClientDealComment({ clientId, dealId, text });
            setNewText("");
            await getClientDeals({ clientId });
        } finally {
            setAddPending(false);
        }
    };

    const startEdit = (comment: Comment) => {
        setEditingId(comment.id);
        setEditingText(comment.text);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingText("");
    };

    const saveEdit = async () => {
        const text = editingText.trim();
        if (!editingId || !text) return;

        try {
            setEditPending(true);
            await updateClientDealComment({
                commentId: editingId,
                text,
            });
            await getClientDeals({ clientId });
            setEditingId(null);
            setEditingText("");
        } finally {
            setEditPending(false);
        }
    };

    const handleDelete = async (commentId: number | string) => {
        try {
            await deleteClientDealComment({ commentId });
            await getClientDeals({ clientId });
        } catch (e) {
            console.error("Не удалось удалить комментарий", e);
        }
    };

    const hasComments = Array.isArray(comments) && comments.length > 0;

    return (
        <div className="mt-2 space-y-2">
            {/* Список комментариев */}
            {hasComments && (
                <div className="space-y-1">
                    {comments.map((c) => {
                        const isEditing = editingId === c.id;

                        return (
                            <div
                                key={c.id}
                                className="bg-gray-50 rounded px-2 py-1 flex items-start gap-2"
                            >
                                <div className="flex-1 min-w-0">
                                    {isEditing ? (
                                        <textarea
                                            className="w-full border rounded px-2 py-1 text-xs resize-none"
                                            rows={2}
                                            value={editingText}
                                            onChange={(e) => setEditingText(e.target.value)}
                                        />
                                    ) : (
                                        <p className="text-xs text-gray-700 whitespace-pre-line">
                                            {c.text}
                                        </p>
                                    )}

                                    {isEditing && (
                                        <div className="flex gap-2 mt-1">
                                            <Button
                                                type="button"
                                                variant="link"
                                                text={editPending ? "Сохраняем…" : "Сохранить"}
                                                onClick={saveEdit}
                                                disabled={editPending || !editingText.trim()}
                                                className="text-[11px] disabled:opacity-60"
                                            />

                                            <Button
                                                type="button"
                                                variant="muted"
                                                text="Отмена"
                                                onClick={cancelEdit}
                                                disabled={editPending}
                                                className="text-[11px]"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Иконки действий — на десктопе в строку */}
                                {!isEditing && (
                                    <div className="flex flex-row gap-1 sm:flex-row">
                                        <button
                                            type="button"
                                            className="flex-shrink-0 p-1 rounded hover:bg-gray-100"
                                            title="Редактировать комментарий"
                                            onClick={() => startEdit(c)}
                                        >
                                            <EditIcon
                                                width={18}
                                                height={18}
                                                stroke="#818c99"
                                            />
                                        </button>
                                        <button
                                            type="button"
                                            className="flex-shrink-0 p-1 rounded hover:bg-gray-100"
                                            title="Удалить комментарий"
                                            onClick={() => handleDelete(c.id)}
                                        >
                                            <DeleteIcon
                                                width={18}
                                                height={18}
                                                stroke="#818c99"
                                            />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Форма добавления нового комментария */}
            <form
                className="mt-2 border-t border-gray-100 pt-2 space-y-1"
                onSubmit={handleAdd}
            >
        <textarea
            className="w-full border rounded px-2 py-1 text-xs resize-none"
            rows={2}
            placeholder="Добавить комментарий по сделке"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
        />

                <div className="flex justify-end">
                    <Button
                        type="submit"
                        text={addPending ? "Добавляем…" : "Добавить комментарий"}
                        pending={addPending}
                        disabled={!newText.trim()}
                        variant="primary"
                        className="inline-flex items-center justify-center px-3 py-1 rounded text-xs min-w-0"
                    />

                </div>
            </form>
        </div>
    );
};

const mapDispatchToProps: DispatchProps = {
    addClientDealComment: UserActionCreator.addClientDealComment as any,
    updateClientDealComment: UserActionCreator.updateClientDealComment as any,
    deleteClientDealComment: UserActionCreator.deleteClientDealComment as any,
    getClientDeals: UserActionCreator.getClientDeals as any,
};

export const DealComments = connect(
    null,
    mapDispatchToProps,
)(_DealComments);

